<script setup>
import { ref } from 'vue'
import { updateStoredVersion } from '../utils/version'

const showUpdateNotice = ref(false)
const updateInfo = ref(null)
const isUpdating = ref(false)
let ignoreNextCheck = false

function closeUpdateNotice() {
  if (isUpdating.value) return
  showUpdateNotice.value = false
  ignoreNextCheck = true
  setTimeout(() => {
    ignoreNextCheck = false
  }, 1000)
}

async function applyUpdate() {
  if (!updateInfo.value?.version) return
  try {
    isUpdating.value = true
    ignoreNextCheck = true
    updateStoredVersion(updateInfo.value.version)
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(key => caches.delete(key)))
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(registration => registration.unregister()))
    }
    const next = new URL('/', window.location.origin)
    next.searchParams.set('updated', updateInfo.value.version)
    window.location.replace(next.toString())
  } catch (error) {
    console.error('Erreur lors de la mise à jour:', error)
    window.location.replace('/')
  }
}

function showNotice(versionInfo) {
  if (isUpdating.value || ignoreNextCheck || showUpdateNotice.value) return
  updateInfo.value = versionInfo
  showUpdateNotice.value = true
}

defineExpose({ showNotice })
</script>

<template><Transition name="update-notice">
    <div v-if="showUpdateNotice" class="bottom-right-cookie-container">
      <v-card class="update-card" :elevation="10">
        <div class="update-header">
          <div class="update-icon">
            <v-icon icon="mdi-update" color="white" size="large" />
          </div>
          <div class="update-title-section">
            <div class="update-title">Update available</div>
            <div v-if="updateInfo" class="update-build">Version {{ updateInfo.version }}</div>
          </div>
        </div>
        <div class="update-content">
          <p>A new version of the website is available. Update now to get the latest fixes and improvements.</p>
        </div>
        <div class="update-actions">
          <v-btn type="button" class="accept-btn" :loading="isUpdating" :disabled="isUpdating" @click="applyUpdate">
            <v-icon icon="mdi-refresh" size="small" class="mr-1" />
            {{ isUpdating ? 'Updating...' : 'Update now' }}
          </v-btn>
          <v-btn class="later-btn" text :disabled="isUpdating" @click="closeUpdateNotice">
            Later
          </v-btn>
        </div>
      </v-card>
    </div>
  </Transition></template>

<style scoped>
.bottom-right-cookie-container {
  position:fixed;
  bottom:20px;
  right:20px;
  z-index:9999;
  width:320px}
.update-card {
  border-radius:12px!important;
  width:100%;
  overflow:hidden;
  background-color:var(--glass)!important;
  border:1px solid var(--line)!important;
  box-shadow:0 20px 50px #0000008c!important}
.update-header {
  display:flex;
  align-items:center;
  padding:16px}
.update-icon {
  background:#ffffff1a;
  border-radius:10px;
  width:40px;
  height:40px;
  display:flex;
  align-items:center;
  justify-content:center;
  margin-right:12px}
.update-title-section {
  flex:1}
.update-title {
  font-size:.95rem;
  font-weight:700;
  letter-spacing:.02em;
  text-transform:uppercase;
  color:var(--ink)}
.update-build {
  margin-top:2px;
  font-size:.78rem;
  font-weight:500;
  letter-spacing:.02em;
  color:var(--mute)}
.update-content {
  padding:0 16px 16px;
  color:var(--ink-soft);
  font-size:.86rem}
.update-content p {
  margin:0;
  font-size:.85rem;
  line-height:1.5;
  color:var(--ink)}
.update-actions {
  display:flex;
  padding:12px 16px;
  gap:8px;
  border-top:1px solid var(--line);
  background-color:var(--glass)}
.accept-btn {
  flex:1;
  background:var(--hover-color)!important;
  color:var(--ink)!important;
  font-weight:700!important;
  text-transform:none!important;
  border-radius:10px!important}
.later-btn {
  color:var(--ink-soft)!important;
  text-transform:none!important;
  font-weight:700!important}
.update-notice-enter-active,
.update-notice-leave-active {
  transition:opacity .28s ease,transform .28s ease}
.update-notice-enter-from,
.update-notice-leave-to {
  opacity:0;
  transform:translateY(12px)}
</style>
